import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText,
  SortAsc,
  Hash,
  GripVertical,
  Target,
  TrendingUp,
  Wallet,
  Check,
  Minus,
  Minimize2,
  Maximize2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { readMapEntry } from '../../utils/judgeScoreDelta';

/**
 * "Tüm Sonuçları Göster" akışında her kaynak tevzi listesi için bir renk paleti.
 * Sıra: ardışık renkler renk çarkında geniş açıyla uzak (max kontrast) seçilmiştir,
 * böylece 2-3-4 kaynak senaryolarında bile listeler görsel olarak net ayrışır.
 *   1. Rose      → 2. Emerald   (190° zıt)
 *   2. Emerald   → 3. Indigo    (95°)
 *   3. Indigo    → 4. Amber     (150°)
 *   4. Amber     → 5. Sky       (165°)
 *   5. Sky       → 6. Pink      (125°)
 *   6. Pink      → 7. Teal      (150°)
 *   7. Teal      → 8. Violet    (95°)
 *
 * Statik class string'leri (Tailwind JIT bunları purge'lemesin diye dinamik üretim YOK).
 */
const SOURCE_COLOR_PALETTE = [
  { row: 'bg-rose-50 hover:bg-rose-100',       borderL: 'border-l-rose-400',    legend: 'bg-rose-50 border-rose-300',       dot: 'bg-rose-500' },
  { row: 'bg-emerald-50 hover:bg-emerald-100', borderL: 'border-l-emerald-400', legend: 'bg-emerald-50 border-emerald-300', dot: 'bg-emerald-500' },
  { row: 'bg-indigo-50 hover:bg-indigo-100',   borderL: 'border-l-indigo-400',  legend: 'bg-indigo-50 border-indigo-300',   dot: 'bg-indigo-500' },
  { row: 'bg-amber-50 hover:bg-amber-100',     borderL: 'border-l-amber-400',   legend: 'bg-amber-50 border-amber-300',     dot: 'bg-amber-500' },
  { row: 'bg-sky-50 hover:bg-sky-100',         borderL: 'border-l-sky-400',     legend: 'bg-sky-50 border-sky-300',         dot: 'bg-sky-500' },
  { row: 'bg-pink-50 hover:bg-pink-100',       borderL: 'border-l-pink-400',    legend: 'bg-pink-50 border-pink-300',       dot: 'bg-pink-500' },
  { row: 'bg-teal-50 hover:bg-teal-100',       borderL: 'border-l-teal-400',    legend: 'bg-teal-50 border-teal-300',       dot: 'bg-teal-500' },
  { row: 'bg-violet-50 hover:bg-violet-100',   borderL: 'border-l-violet-400',  legend: 'bg-violet-50 border-violet-300',   dot: 'bg-violet-500' },
];

/** Hakim kartı sırasını sürükleyerek güncelle: hedefin önüne yerleşir */
function reorderJudgeIds(order, draggedId, targetId) {
  const d = String(draggedId);
  const t = String(targetId);
  if (d === t) return order;
  const without = order.filter((id) => String(id) !== d);
  const ti = without.findIndex((id) => String(id) === t);
  if (ti === -1) return [...without, d];
  const next = [...without];
  next.splice(ti, 0, d);
  return next;
}

function parseDropPayload(raw) {
  try {
    const p = JSON.parse(raw || '{}');
    if (p && typeof p === 'object') {
      if (p.kind === 'judge-card' && p.judgeId != null) {
        return { kind: 'judge-card', judgeId: p.judgeId };
      }
      if (p.kind === 'file' && p.fileId != null) {
        return { kind: 'file', fileId: p.fileId, kaynakHakim: p.kaynakHakim };
      }
      if (p.fileId != null) {
        return { kind: 'file', fileId: p.fileId, kaynakHakim: p.kaynakHakim };
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

const JudgeDetails = ({
  distributedFiles,
  judges,
  calculationSettings,
  distributionResults,
  summaryReport,
  dosyaSayisiTevziOncesiByJudgeId,
  judgeDetailsSortOrder,
  setJudgeDetailsSortOrder,
  judgeDetailsCardOrder,
  setJudgeDetailsCardOrder,
  handleFileClick,
  isAssignedToFormerJudge,
  isAssignedToManualJudge,
  onReassignFileToJudge,
  onEditJudge,
  scrollToJudgeId,
  onScrollToJudgeHandled,
}) => {
  // MainTable'dan "Atanan Hakim" tıklamasıyla gelen scroll + highlight isteği.
  // Kompakt moddaki kartlar genişletilir, scroll edilir, kısa indigo halka efekti uygulanır.
  useEffect(() => {
    if (scrollToJudgeId == null) return;
    const id = String(scrollToJudgeId);
    // Kompakt moddaysa o kartı aç:
    setExpandedJudgeIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    // Bir sonraki render'da kartı bul ve scroll et:
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-judge-card-id="${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-indigo-400', 'ring-offset-2', 'ring-offset-white');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-indigo-400', 'ring-offset-2', 'ring-offset-white');
        }, 1800);
      }
      if (onScrollToJudgeHandled) onScrollToJudgeHandled();
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToJudgeId]);

  const [dragOverHakim, setDragOverHakim] = useState(null);
  const [draggingJudgeId, setDraggingJudgeId] = useState(null);
  const [dragOverReorderTargetId, setDragOverReorderTargetId] = useState(null);
  /** dragOver sırasında state henüz güncellenmemiş olabileceği için */
  const draggingJudgeIdRef = useRef(null);

  /**
   * Minimal/kompakt görünüm: true iken tüm dosya listeleri varsayılan olarak gizlenir.
   * Kullanıcı tercihi localStorage'da kalıcı tutulur.
   */
  const [isCompactMode, setIsCompactMode] = useState(
    () => localStorage.getItem('judgeDetailsCompactMode') === 'true'
  );
  /** Kompakt moddayken kart bazında genişletilenler (kart üzerinden açar — kalıcı değil). */
  const [expandedJudgeIds, setExpandedJudgeIds] = useState(() => new Set());
  /**
   * Geniş ekranda kaç kart yan yana gösterilsin — kullanıcı tercihi localStorage'da
   * kalıcı tutulur (3/4/5).
   */
  const [cardColumns, setCardColumns] = useState(() => {
    const saved = Number(localStorage.getItem('judgeDetailsCardColumns'));
    return [3, 4, 5].includes(saved) ? saved : 3;
  });

  const handleCardColumnsChange = (n) => {
    setCardColumns(n);
    localStorage.setItem('judgeDetailsCardColumns', String(n));
  };

  // "Tüm Sonuçları Göster" akışında dosyalar farklı tevzilerden gelir; her bir
  // sourceFileName için renk haritası (≥2 farklı kaynak varsa renklendirme yapılır).
  // Tek tevzili görünümlerde null döner → eski beyaz arka plan kalır.
  const sourceColorMap = useMemo(() => {
    const seen = [];
    const seenSet = new Set();
    for (const f of distributedFiles) {
      const src = f && f.sourceFileName;
      if (!src || seenSet.has(src)) continue;
      seenSet.add(src);
      seen.push(src);
    }
    if (seen.length < 2) return null;
    const map = new Map();
    seen.forEach((src, idx) => {
      map.set(src, SOURCE_COLOR_PALETTE[idx % SOURCE_COLOR_PALETTE.length]);
    });
    return map;
  }, [distributedFiles]);

  // Tailwind JIT için statik class haritası (dinamik string üretme — purge sorunu olur).
  const gridClassByColumns = {
    3: 'grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3',
    4: 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    5: 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
  };
  const gridClass = gridClassByColumns[cardColumns] || gridClassByColumns[3];

  const toggleCompactMode = () => {
    setIsCompactMode((prev) => {
      const next = !prev;
      // Kompakt moda geçerken tek tek genişletmeleri sıfırla → tüm kartlar kapalı başlar.
      if (next) setExpandedJudgeIds(new Set());
      localStorage.setItem('judgeDetailsCompactMode', String(next));
      return next;
    });
  };

  const toggleExpandJudge = (judgeId) => {
    setExpandedJudgeIds((prev) => {
      const next = new Set(prev);
      const id = String(judgeId);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const computeYukPuani = (dosyalar) =>
    dosyalar.reduce((sum, dosya) => {
      if (isAssignedToFormerJudge(dosya)) {
        return sum + dosya.dosyaPuani * calculationSettings.hakimKendiDosyasiCarpani;
      }
      return sum + dosya.dosyaPuani;
    }, 0);

  const computeHamToplam = (dosyalar) =>
    dosyalar.reduce((sum, dosya) => sum + (dosya.dosyaPuani || 0), 0);

  const getDosyaSayisiOncekiVeYeni = (judge, dosyalar) => {
    const n = dosyalar.length;
    const fromBaseline = readMapEntry(dosyaSayisiTevziOncesiByJudgeId, judge.id);
    let onceki;
    if (fromBaseline !== undefined && fromBaseline !== null) {
      const b = Number(fromBaseline);
      onceki = Number.isFinite(b) ? b : 0;
    } else {
      const stored = Number(judge.dosyaSayisi) || 0;
      onceki = Math.max(0, stored - n);
    }
    const yeni = onceki + n;
    return { oncekiDosyaSayisi: onceki, yeniDosyaSayisi: yeni };
  };

  const judgeDetails = judges.map((judge) => {
    const dosyalar = distributedFiles.filter((f) => f.atananHakim === judge.name);
    const toplamHam = computeHamToplam(dosyalar);
    const toplamYuk = computeYukPuani(dosyalar);
    const yeniMap =
      distributionResults?.yeniHakimPuanlari ?? summaryReport?.yeniHakimPuanlari;

    const yeniFromResults = yeniMap?.[judge.id];
    const tevziSonrasi =
      yeniFromResults !== undefined && yeniFromResults !== null
        ? yeniFromResults
        : judge.score ?? 0;
    const tevziOncesi =
      dosyalar.length > 0 ? tevziSonrasi - toplamYuk : tevziSonrasi;

    const { oncekiDosyaSayisi, yeniDosyaSayisi } = getDosyaSayisiOncekiVeYeni(judge, dosyalar);

    return {
      judge,
      hakimAdi: judge.name,
      dosyalar,
      toplamHamDosyaPuani: toplamHam,
      toplamYukPuani: toplamYuk,
      tevziSonrasi,
      tevziOncesi,
      oncekiDosyaSayisi,
      yeniDosyaSayisi,
    };
  });

  const sortJudgeDetails = (detailsArray, sortOrder) => {
    return [...detailsArray].sort((a, b) => {
      if (sortOrder === 'alphabetical') {
        return a.hakimAdi.localeCompare(b.hakimAdi, 'tr');
      }
      if (sortOrder === 'fileCount') {
        return b.dosyalar.length - a.dosyalar.length;
      }
      return b.toplamYukPuani - a.toplamYukPuani;
    });
  };

  const algorithmSortedJudgeDetails = sortJudgeDetails(judgeDetails, judgeDetailsSortOrder);

  const sortedJudgeDetails = (() => {
    if (!judgeDetailsCardOrder?.length) return algorithmSortedJudgeDetails;
    const byId = new Map(
      algorithmSortedJudgeDetails.map((d) => [String(d.judge.id), d])
    );
    const ordered = [];
    const seen = new Set();
    judgeDetailsCardOrder.forEach((id) => {
      const d = byId.get(String(id));
      if (d) {
        ordered.push(d);
        seen.add(String(id));
      }
    });
    algorithmSortedJudgeDetails.forEach((d) => {
      if (!seen.has(String(d.judge.id))) ordered.push(d);
    });
    return ordered;
  })();

  const handleFileDragStart = (e, fileId, kaynakHakim) => {
    e.stopPropagation();
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ kind: 'file', fileId, kaynakHakim })
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleJudgeCardDragStart = (e, judgeId) => {
    e.stopPropagation();
    const id = String(judgeId);
    draggingJudgeIdRef.current = id;
    setDraggingJudgeId(id);
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ kind: 'judge-card', judgeId })
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleJudgeCardDragEnd = () => {
    draggingJudgeIdRef.current = null;
    setDraggingJudgeId(null);
    setDragOverReorderTargetId(null);
    setDragOverHakim(null);
  };

  const handleDragOverZone = (e, hakimAdi, judgeId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggingJudgeIdRef.current) {
      setDragOverReorderTargetId(String(judgeId));
      setDragOverHakim(null);
    } else {
      setDragOverHakim(hakimAdi);
      setDragOverReorderTargetId(null);
    }
  };

  const handleDragLeaveZone = (e, hakimAdi, judgeId) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverHakim((prev) => (prev === hakimAdi ? null : prev));
      setDragOverReorderTargetId((prev) => (prev === String(judgeId) ? null : prev));
    }
  };

  const handleDropOnJudge = (e, hedefHakimAdi, hedefJudgeId) => {
    e.preventDefault();
    setDragOverHakim(null);
    setDragOverReorderTargetId(null);
    const raw = e.dataTransfer.getData('application/json');
    const payload = parseDropPayload(raw);

    if (payload?.kind === 'judge-card') {
      if (payload.judgeId == null || hedefJudgeId == null) return;
      if (String(payload.judgeId) === String(hedefJudgeId)) return;
      const baseIds = algorithmSortedJudgeDetails.map((d) => String(d.judge.id));
      const currentIds = judgeDetailsCardOrder?.length
        ? [...judgeDetailsCardOrder]
        : baseIds;
      const next = reorderJudgeIds(currentIds, payload.judgeId, hedefJudgeId);
      setJudgeDetailsCardOrder(next);
      draggingJudgeIdRef.current = null;
      setDraggingJudgeId(null);
      return;
    }

    if (payload?.kind === 'file') {
      const { fileId, kaynakHakim } = payload;
      if (fileId == null || !hedefHakimAdi || kaynakHakim === hedefHakimAdi) return;
      onReassignFileToJudge(fileId, hedefHakimAdi);
    }
  };

  const fmt = (n) => (typeof n === 'number' && !Number.isNaN(n) ? n.toFixed(2) : '—');

  const hedefMapGlobal =
    distributionResults?.hakimHedefPuanlari ?? summaryReport?.hakimHedefPuanlari;

  const hedefOzeti = (() => {
    if (!hedefMapGlobal || typeof hedefMapGlobal !== 'object') return null;
    const vals = judges
      .map((j) => {
        const v = hedefMapGlobal[j.id] ?? hedefMapGlobal[String(j.id)];
        return v !== undefined && v !== null ? Number(v) : null;
      })
      .filter((n) => n !== null && !Number.isNaN(n));
    if (vals.length === 0) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    if (min === max) return { text: fmt(min), allEqual: true };
    return { text: `${fmt(min)} – ${fmt(max)}`, allEqual: false };
  })();

  return (
    <div className="mt-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center text-xl font-bold text-slate-800">
            <FileText className="mr-2 h-6 w-6 text-indigo-600" />
            Hakim Dosya Detayları
          </h2>
          <p className="mt-1 max-w-xl text-sm text-slate-600">
            Dosyayı başka hakime taşımak için listeden sürükleyin; hakim kartının solundaki tutamacı sürükleyerek
            kart sırasını değiştirin. Üstteki sıralama düğmeleri manuel sırayı sıfırlar.
          </p>
          {hedefOzeti && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-100 bg-amber-50/90 px-3 py-2 text-sm text-amber-950">
              <Target className="h-4 w-4 shrink-0 text-amber-700" aria-hidden />
              <span className="font-medium text-amber-900">
                {hedefOzeti.allEqual ? 'Hedef yük puanı (tevzi):' : 'Hedef yük puanı aralığı (tevzi):'}
              </span>
              <span className="tabular-nums font-semibold text-amber-950" title="Dağıtım için hesaplanan hedef">
                {hedefOzeti.text}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
          <span className="mr-1 hidden text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:inline">
            Sırala
          </span>
          <button
            type="button"
            onClick={() => setJudgeDetailsSortOrder('alphabetical')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
              judgeDetailsSortOrder === 'alphabetical'
                ? 'bg-indigo-600 text-white shadow'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
            aria-pressed={judgeDetailsSortOrder === 'alphabetical'}
            title="Hakim adına göre A → Z sırala"
          >
            <SortAsc size={15} aria-hidden />
            Alfabetik
          </button>
          <button
            type="button"
            onClick={() => setJudgeDetailsSortOrder('score')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
              judgeDetailsSortOrder === 'score'
                ? 'bg-indigo-600 text-white shadow'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
            aria-pressed={judgeDetailsSortOrder === 'score'}
            title="Yük puanına göre büyükten küçüğe sırala"
          >
            <Hash size={15} aria-hidden />
            Puan
          </button>
          <button
            type="button"
            onClick={() => setJudgeDetailsSortOrder('fileCount')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
              judgeDetailsSortOrder === 'fileCount'
                ? 'bg-indigo-600 text-white shadow'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
            aria-pressed={judgeDetailsSortOrder === 'fileCount'}
            title="Dosya sayısına göre büyükten küçüğe sırala"
          >
            <FileText size={15} aria-hidden />
            Dosya
          </button>
          <div className="mx-1 h-6 w-px bg-slate-300/70" aria-hidden />
          <button
            type="button"
            onClick={toggleCompactMode}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
              isCompactMode
                ? 'bg-indigo-600 text-white shadow'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
            title={
              isCompactMode
                ? 'Kompakt görünümden çık — tüm dosya listelerini göster'
                : 'Kompakt görünüm — sadece hakim özetlerini göster'
            }
            aria-pressed={isCompactMode}
          >
            {isCompactMode ? <Maximize2 size={15} aria-hidden /> : <Minimize2 size={15} aria-hidden />}
            {isCompactMode ? 'Geniş' : 'Kompakt'}
          </button>
          <div className="mx-1 h-6 w-px bg-slate-300/70" aria-hidden />
          <span className="hidden text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:inline">
            Sütun
          </span>
          {[3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => handleCardColumnsChange(n)}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-xs font-semibold tabular-nums transition ${
                cardColumns === n
                  ? 'bg-indigo-600 text-white shadow'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
              aria-pressed={cardColumns === n}
              aria-label={`${n} sütun göster`}
              title={`Geniş ekranda ${n} kart yan yana göster`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {sourceColorMap && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
          <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Kaynak listeler
          </span>
          {Array.from(sourceColorMap.entries()).map(([src, c]) => (
            <span
              key={src}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium text-slate-700 ${c.legend}`}
              title={src}
            >
              <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${c.dot}`} aria-hidden />
              <span className="max-w-[220px] truncate">{src}</span>
            </span>
          ))}
        </div>
      )}

      <div className={gridClass}>
        {sortedJudgeDetails.map(
          ({
            judge,
            hakimAdi,
            dosyalar,
            toplamHamDosyaPuani,
            toplamYukPuani,
            tevziSonrasi,
            tevziOncesi,
            oncekiDosyaSayisi,
            yeniDosyaSayisi,
          }) => (
            <div
              key={hakimAdi}
              data-judge-card-id={judge.id}
              role="region"
              aria-label={`${hakimAdi} dosya listesi`}
              onDragOver={(e) => handleDragOverZone(e, hakimAdi, judge.id)}
              onDragLeave={(e) => handleDragLeaveZone(e, hakimAdi, judge.id)}
              onDrop={(e) => handleDropOnJudge(e, hakimAdi, judge.id)}
              className={`overflow-hidden rounded-xl border-2 bg-white shadow-md transition duration-200 ${
                dragOverReorderTargetId === String(judge.id) && draggingJudgeId
                  ? 'scale-[1.01] border-amber-500 ring-2 ring-amber-200'
                  : dragOverHakim === hakimAdi && !draggingJudgeId
                    ? 'scale-[1.01] border-indigo-500 ring-2 ring-indigo-200'
                    : draggingJudgeId === String(judge.id)
                      ? 'opacity-90 border-slate-300'
                      : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="relative border-b border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/90">
                <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
                  <span
                    className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border shadow-sm ${
                      judge.isAvailableForAssignment !== false
                        ? 'border-emerald-300/90 bg-emerald-50 text-emerald-700'
                        : 'border-red-300/90 bg-red-50 text-red-600'
                    }`}
                    title={
                      judge.isAvailableForAssignment !== false
                        ? 'Tevziye açık'
                        : 'Tevziye kapalı'
                    }
                  >
                    {judge.isAvailableForAssignment !== false ? (
                      <Check className="h-3 w-3 stroke-[2.5]" aria-hidden />
                    ) : (
                      <Minus className="h-3 w-3 stroke-[2.5]" aria-hidden />
                    )}
                  </span>
                  {judge.assignmentRatio != null && String(judge.assignmentRatio).trim() !== '' && (
                    <span
                      className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full border border-indigo-200/80 bg-indigo-50 px-2 text-[10px] font-semibold tabular-nums leading-none text-indigo-800 shadow-sm"
                      title="Tevzi oranı"
                    >
                      {String(judge.assignmentRatio).trim()}
                    </span>
                  )}
                </div>
                <div className="flex items-start gap-1.5 px-3 pt-3 pr-[4.75rem]">
                  <button
                    type="button"
                    draggable
                    onDragStart={(e) => handleJudgeCardDragStart(e, judge.id)}
                    onDragEnd={handleJudgeCardDragEnd}
                    className="mt-0.5 shrink-0 cursor-grab rounded-md p-1 text-slate-400 transition hover:bg-white/70 hover:text-slate-700 active:cursor-grabbing"
                    title="Kart sırasını değiştirmek için sürükleyin"
                    aria-label="Hakim kartının yerini değiştirmek için sürükleyin"
                    onClick={(e) => e.preventDefault()}
                  >
                    <GripVertical className="h-5 w-5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => onEditJudge(judge)}
                    className="min-w-0 flex-1 rounded-lg px-1 py-0.5 text-left text-lg font-semibold text-slate-900 transition hover:bg-white/60 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  >
                    {hakimAdi}
                  </button>
                </div>

                {isCompactMode && (
                  <div className="mt-3 space-y-1.5 border-t border-slate-200/80 bg-white/50 px-3 pb-2.5 pt-3">
                    {/* Ana vurgu: bu tevzide alınan yük (dosya sayısı + puan) */}
                    <div className="flex justify-center">
                      <span
                        className="inline-flex items-baseline gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-900 shadow-sm"
                        title="Bu tevzide bu hakime atanan dosya sayısı ve toplam yük puanı"
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0 self-center text-emerald-700" aria-hidden />
                        <span className="tabular-nums text-base font-bold leading-none">{dosyalar.length}</span>
                        <span className="text-[11px] font-medium text-emerald-700">dosya</span>
                        <span className="mx-0.5 text-emerald-300" aria-hidden>·</span>
                        <span className="tabular-nums text-base font-bold leading-none">{fmt(toplamYukPuani)}</span>
                        <span className="text-[11px] font-medium text-emerald-700">puan</span>
                      </span>
                    </div>

                    {/* İkincil: önce → sonra (toplam puan ve dosya sayısı) */}
                    <div className="flex flex-wrap items-baseline justify-center gap-x-2.5 gap-y-0.5 text-[11px] text-slate-500">
                      <span className="inline-flex items-baseline gap-1">
                        <span>Puan:</span>
                        <span className="tabular-nums font-medium text-slate-600">{fmt(tevziOncesi)}</span>
                        <span className="text-slate-300" aria-hidden>→</span>
                        <span className="tabular-nums font-semibold text-indigo-700">{fmt(tevziSonrasi)}</span>
                      </span>
                      <span className="text-slate-300" aria-hidden>•</span>
                      <span className="inline-flex items-baseline gap-1">
                        <span>Dosya:</span>
                        <span className="tabular-nums font-medium text-slate-600">{oncekiDosyaSayisi}</span>
                        <span className="text-slate-300" aria-hidden>→</span>
                        <span className="tabular-nums font-semibold text-slate-700">{yeniDosyaSayisi}</span>
                      </span>
                    </div>
                  </div>
                )}

                {!isCompactMode && (
                <dl className="mt-3 border-t border-slate-200/80 bg-white/40 px-2 py-2.5">
                  <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] items-center gap-x-3 gap-y-1">
                    <div className="min-w-0" aria-hidden />
                    <dt className="flex items-center justify-center gap-0.5 border-r border-r-slate-200/80 pb-2 pr-2 text-center text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      <Wallet className="h-3 w-3 shrink-0" aria-hidden />
                      Önce
                    </dt>
                    <dt className="flex items-center justify-center gap-0.5 pb-2 pl-2 text-center text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      <TrendingUp className="h-3 w-3 shrink-0" aria-hidden />
                      Sonra
                    </dt>

                    <span className="justify-self-start self-center rounded-full border border-slate-200/90 bg-slate-100/95 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-600 shadow-sm">
                      Puan
                    </span>
                    <dd
                      className="m-0 flex min-h-[2.5rem] items-center justify-center border-r border-r-slate-200/80 pr-2 text-sm font-bold tabular-nums text-slate-800"
                      title="Bu tevzi öncesi toplam puan"
                    >
                      {fmt(tevziOncesi)}
                    </dd>
                    <dd
                      className="m-0 flex min-h-[2.5rem] items-center justify-center pl-2 text-sm font-bold tabular-nums text-indigo-800"
                      title="Tevzi sonrası toplam puan"
                    >
                      {fmt(tevziSonrasi)}
                    </dd>

                    <span className="justify-self-start self-center rounded-full border border-slate-200/90 bg-slate-100/95 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-600 shadow-sm">
                      Dosya
                    </span>
                    <dd
                      className="m-0 flex min-h-[2.75rem] items-center justify-center border-r border-r-slate-200/80 border-t border-dashed border-t-slate-200/90 pr-2 pt-2 text-lg font-bold tabular-nums leading-tight text-slate-800"
                      title={`Genel hakim kaydı: bu tevziden önce ${oncekiDosyaSayisi} dosya`}
                    >
                      {oncekiDosyaSayisi}
                    </dd>
                    <dd
                      className="m-0 flex min-h-[2.75rem] items-center justify-center border-t border-dashed border-t-indigo-200/70 pl-2 pt-2 text-lg font-bold tabular-nums leading-tight text-emerald-900"
                      title={`Genel hakim kaydı: bu tevziden sonra toplam ${yeniDosyaSayisi} dosya`}
                    >
                      {yeniDosyaSayisi}
                    </dd>
                  </div>
                </dl>
                )}

                {!isCompactMode && (
                <div className="flex flex-nowrap items-stretch gap-2 overflow-x-auto px-2 pb-3 pt-1">
                  <div
                    className="min-w-0 flex-1 rounded-lg border border-indigo-100 bg-white px-2.5 py-2 shadow-sm"
                    title="Ham dosya puanları toplamı / kurala göre (eski dosyada çarpan) yük"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
                      Bu tevzide
                    </p>
                    <div className="mt-1 flex flex-nowrap items-baseline justify-center gap-1.5">
                      <span className="text-base font-bold tabular-nums text-slate-900 sm:text-lg">
                        {fmt(toplamHamDosyaPuani)}
                      </span>
                      <span className="text-slate-300" aria-hidden>
                        /
                      </span>
                      <span className="text-base font-bold tabular-nums text-emerald-700 sm:text-lg">
                        {fmt(toplamYukPuani)}
                      </span>
                    </div>
                    <p className="mt-1 text-center text-[10px] leading-tight text-slate-500">
                      Ham toplam · çarpanlı yük
                    </p>
                  </div>
                  <div
                    className="flex min-w-[4.5rem] shrink-0 flex-col items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center shadow-sm"
                    title="Bu tevzide bu hakime atanan dosya sayısı"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                      Dosya
                    </span>
                    <span className="text-xl font-bold tabular-nums leading-tight text-emerald-900">
                      {dosyalar.length}
                    </span>
                    <span className="mt-1 max-w-full text-[9px] leading-tight text-emerald-700/90">
                      bu tevzide
                    </span>
                  </div>
                </div>
                )}
              </div>

              {isCompactMode && (
                <button
                  type="button"
                  onClick={() => toggleExpandJudge(judge.id)}
                  className="flex w-full items-center justify-center gap-1.5 border-b border-slate-100 bg-slate-50/60 px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-indigo-50 hover:text-indigo-700"
                  aria-expanded={expandedJudgeIds.has(String(judge.id))}
                >
                  {expandedJudgeIds.has(String(judge.id)) ? (
                    <>
                      <ChevronUp size={14} aria-hidden />
                      Dosyaları gizle
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} aria-hidden />
                      Dosyaları göster ({dosyalar.length})
                    </>
                  )}
                </button>
              )}

              {(!isCompactMode || expandedJudgeIds.has(String(judge.id))) && (
              <div className="min-h-[120px] p-3">
                {dosyalar.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-500">Dosya yok — buraya bırakın</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {dosyalar.map((dosya) => {
                      const srcColor = sourceColorMap && dosya.sourceFileName
                        ? sourceColorMap.get(dosya.sourceFileName)
                        : null;
                      const liClass = srcColor
                        ? `cursor-grab rounded-md border-l-4 px-2 py-2 active:cursor-grabbing ${srcColor.row} ${srcColor.borderL}`
                        : 'cursor-grab rounded-md py-2 active:cursor-grabbing hover:bg-slate-50';
                      const liTitle = dosya.sourceFileName
                        ? `Kaynak: ${dosya.sourceFileName} — başka hakime sürükleyin`
                        : 'Başka hakime sürükleyin';
                      return (
                      <li
                        key={dosya.id}
                        draggable
                        onDragStart={(e) => handleFileDragStart(e, dosya.id, hakimAdi)}
                        onDragEnd={() => setDragOverHakim(null)}
                        className={liClass}
                        title={liTitle}
                      >
                        <div className="flex items-center justify-between gap-2 px-0.5">
                          <div className="flex min-w-0 items-center">
                            <GripVertical className="mr-1 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                            <span
                              className="truncate text-sm font-medium text-slate-900 hover:text-indigo-600"
                              onClick={() => handleFileClick(dosya)}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') {
                                  ev.preventDefault();
                                  handleFileClick(dosya);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {dosya.esasNo}
                            </span>
                            {isAssignedToFormerJudge(dosya) && (
                              <span
                                className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white"
                                title="Eski dosya (çarpan uygulanır)"
                              >
                                K
                              </span>
                            )}
                            {isAssignedToManualJudge && isAssignedToManualJudge(dosya) && (
                              <span
                                className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white"
                                title="Manuel tevzi (elle atandı)"
                              >
                                M
                              </span>
                            )}
                          </div>
                          <span className="shrink-0 text-sm tabular-nums text-slate-600">
                            {dosya.dosyaPuani.toFixed(2)}
                          </span>
                        </div>
                      </li>
                    );
                    })}
                  </ul>
                )}
              </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default JudgeDetails;
